// src/components/pages/blotter/converts/Converts_Blotter.js
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Cancel, Edit, Save } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { fetchConvertsBlotter, submitConvertBlotterTrades, updateBlotterEntry } from "../../../../services/Axios";
import {
  buildSubmitResultRows,
  formatHedgeTradeError,
  submitTradesSequentially,
} from "../../../../utils/submitTrades";
import { useAuth } from "../../../../context/AuthContext";
import { TabNavigation } from "../../../ui";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Trade submission modes — must match the backend's `?mode=` query param
// (realtime_app/views.py `enter_trades`). "done" books directly as a done
// trade; "v2" creates a working order instead. No automatic fallback between
// them: a failed done trade must be retried manually as a working order.
const MODE_DONE = "done";
const MODE_V2 = "v2";

// Display config for `hedge_trade_status` (set by the backend after a hedge
// submission attempt). Label text differs per state so status is never
// conveyed by chip colour alone; colours come from the MUI theme palette.
export const HEDGE_TRADE_STATUS_CONFIG = {
  ENTERED: { label: "Entered", color: "default" },
  ENTERED_WITH_WARNINGS: { label: "Entered w/ Warnings", color: "warning" },
  FAILED: { label: "Failed", color: "error" },
};

// Table headers configuration
const TABLE_HEADERS = [
  { id: "trade_id", label: "Trade ID", mobile: false },
  { id: "trade_date", label: "Trade Date", mobile: true },
  { id: "strategy_name", label: "Strategy Name", mobile: false },
  { id: "action", label: "Action", mobile: true },
  { id: "security_symbol", label: "Security Symbol", mobile: true },
  { id: "price", label: "Price", mobile: true },
  { id: "quantity", label: "Quantity", mobile: true },
  { id: "BrokerCode", label: "Broker", mobile: true },
  { id: "hedge_action", label: "Hedge Action", mobile: true },
  { id: "hedge_symbol", label: "Hedge Symbol", mobile: true },
  { id: "hedge_price", label: "Hedge Price", mobile: true },
  { id: "hedge_quantity", label: "Hedge Quantity", mobile: true },
  { id: "enter_hedge", label: "Enter Hedge", mobile: false },
  { id: "hedge_entered", label: "Hedge Entered", mobile: false },
  { id: "entered_manually", label: "Entered Manually", mobile: false },
  { id: "hedge_trade_status", label: "Trade Status", mobile: true },
];

export default function ConvertsBlotter() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isTrader = claims?.roles?.includes("role.trader");
  const [blotterEntries, setBlotterEntries] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "trade_date",
    direction: "desc",
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [activeTab, setActiveTab] = useState("tab1");
  const [editMode, setEditMode] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submittingTrades, setSubmittingTrades] = useState(false);
  const [submitMode, setSubmitMode] = useState(MODE_DONE);
  const [submitProgress, setSubmitProgress] = useState({ completed: 0, total: 0 });
  const [submitResults, setSubmitResults] = useState(null);

  // Responsive table handling
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 1024);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Memoized filtered and sorted data (MOVED ABOVE to avoid stale references)
  const filteredData = useMemo(() => {
    let data = [...blotterEntries];

    if (globalFilter) {
      data = data.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(globalFilter.toLowerCase()),
        ),
      );
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aValue = a[sortConfig.key] ?? "";
        const bValue = b[sortConfig.key] ?? "";
        const comparison =
          typeof aValue === "string"
            ? aValue.localeCompare(bValue)
            : aValue - bValue;
        return sortConfig.direction === "desc" ? -comparison : comparison;
      });
    }

    return data;
  }, [blotterEntries, globalFilter, sortConfig]);

  // Rows eligible for hedge submission. Computed once and shared by the
  // review table, the disabled check, and the actual submission call so
  // result indices always line up with what the user reviewed.
  const eligibleTradesToSubmit = useMemo(
    () =>
      filteredData.filter(
        (row) =>
          row.enter_hedge &&
          !row.hedge_entered &&
          !row.entered_manually &&
          row.hedge_symbol &&
          row.hedge_price &&
          row.hedge_quantity &&
          row.hedge_action,
      ),
    [filteredData],
  );

  // Fetch data with cleanup
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchConvertsBlotter();
      setBlotterEntries(
        data
          .filter((dat) => {
            if (dat.not_convert) return false;
            if (!dat.created_at) return false;
            const createdDate = new Date(dat.created_at);
            const today = new Date();
            return (
              createdDate.getFullYear() === today.getFullYear() &&
              createdDate.getMonth() === today.getMonth() &&
              createdDate.getDate() === today.getDate()
            );
          })
          .map((entry) => ({ ...entry })),
      );
    } catch (err) {
      console.error("Blotter fetch error:", err);
      setError(`Failed to fetch blotter entries: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sorting handler
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  // Edit mode toggle
  const handleEditModeToggle = useCallback(() => {
    setEditMode((prev) => {
      if (prev) {
        setEditingData({});
        return false;
      }
      const initialEditingData = filteredData.reduce((acc, row) => {
        acc[row.id] = { ...row };
        return acc;
      }, {});
      setEditingData(initialEditingData);
      return true;
    });
  }, [filteredData]);

  const handleCellEdit = useCallback((rowId, field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value },
    }));
  }, []);

  const handleSaveAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all(
        Object.entries(editingData).map(([rowId, data]) =>
          updateBlotterEntry(rowId, data),
        ),
      );
      await fetchData();
      setEditMode(false);
      setEditingData({});
    } catch (error) {
      console.error("Save failed:", error);
      setError(`Failed to save changes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [editingData, fetchData]);

  const handleOpenSubmitDialog = useCallback(() => {
    setSubmitMode(MODE_DONE);
    setSubmitResults(null);
    setSubmitProgress({ completed: 0, total: 0 });
    setSubmitDialogOpen(true);
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    setSubmittingTrades(true);
    setSubmitProgress({ completed: 0, total: eligibleTradesToSubmit.length });
    try {
      const results = await submitTradesSequentially(
        eligibleTradesToSubmit,
        (row) => submitConvertBlotterTrades([row], submitMode),
        (completed, total) => setSubmitProgress({ completed, total }),
      );
      setSubmitResults(buildSubmitResultRows(eligibleTradesToSubmit, results));
    } catch (error) {
      console.error("Submit trades failed:", error);
      setError(`Failed to submit trades: ${error.message}`);
    } finally {
      setSubmittingTrades(false);
    }
  }, [eligibleTradesToSubmit, submitMode]);

  // Explicit dismiss of the results view — only now do we close the dialog
  // and refetch, so the user always sees per-trade outcomes first instead of
  // the dialog silently closing after submission.
  const handleDismissResults = useCallback(() => {
    setSubmitResults(null);
    setSubmitDialogOpen(false);
    fetchData();
  }, [fetchData]);

  const formatFieldValue = (value) =>
    value == null
      ? ""
      : typeof value === "boolean"
        ? value
          ? "Yes"
          : "No"
        : value.toLocaleString();

  // Row tint for `hedge_trade_status` — neutral grey for ENTERED, amber for
  // ENTERED_WITH_WARNINGS, red for FAILED, no tint when null/unset. Colours
  // are derived from the theme palette (never hard-coded hex).
  const getRowTint = useCallback(
    (status) => {
      switch (status) {
        case "ENTERED":
          return alpha(theme.palette.grey[400], 0.15);
        case "ENTERED_WITH_WARNINGS":
          return alpha(theme.palette.warning.main, 0.15);
        case "FAILED":
          return alpha(theme.palette.error.main, 0.12);
        default:
          return undefined;
      }
    },
    [theme],
  );

  // `hedge_trade_status` is backend-set, never user-editable — always
  // rendered as a chip (with the underlying error/warning message in a
  // tooltip) regardless of edit mode.
  const renderHedgeTradeStatusChip = (row) => {
    const status = row.hedge_trade_status;
    if (!status) return null;

    const config = HEDGE_TRADE_STATUS_CONFIG[status];
    if (!config) return status;

    const chip = (
      <Chip label={config.label} color={config.color} size="small" />
    );

    // hedge_trade_error is a Django JSONField (an Eze error list or a
    // { message } dict) — never pass it directly as Tooltip's `title`,
    // which renders it as a React child; format it to a string first.
    const errorMessage = formatHedgeTradeError(row.hedge_trade_error);

    return errorMessage ? (
      <MuiTooltip title={errorMessage}>{chip}</MuiTooltip>
    ) : (
      chip
    );
  };

  const renderEditableCell = useCallback(
    (row, field) => {
      if (field === "hedge_trade_status") {
        return renderHedgeTradeStatusChip(row);
      }

      const value =
        editMode && editingData[row.id]
          ? editingData[row.id][field]
          : row[field];
      const isEditable =
        editMode && !["trade_id", "trade_date"].includes(field);

      if (!isEditable) return formatFieldValue(value);

      if (["action", "hedge_action"].includes(field)) {
        return (
          <FormControl size="small" fullWidth>
            <Select
              value={value || ""}
              onChange={(e) => handleCellEdit(row.id, field, e.target.value)}
              sx={{ fontSize: "0.75rem" }}
            >
              {["Buy", "Sell", "Short", "Cover"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      if (
        ["enter_hedge", "hedge_entered", "entered_manually"].includes(field)
      ) {
        return (
          <Switch
            checked={value || false}
            onChange={(e) => handleCellEdit(row.id, field, e.target.checked)}
            size="small"
          />
        );
      }

      if (["hedge_quantity", "hedge_price"].includes(field)) {
        return (
          <TextField
            size="small"
            type="number"
            value={value || ""}
            onChange={(e) => handleCellEdit(row.id, field, e.target.value)}
            sx={{ fontSize: "0.75rem" }}
            inputProps={{ step: field === "hedge_price" ? "0.01" : "1" }}
          />
        );
      }

      return (
        <TextField
          size="small"
          value={value || ""}
          onChange={(e) => handleCellEdit(row.id, field, e.target.value)}
          sx={{ fontSize: "0.75rem" }}
        />
      );
    },
    [editMode, editingData, handleCellEdit],
  );

  const tabs = [{ value: "tab1", label: "Blotter Entries" }];

  return (
    <Box sx={{ p: { xs: 0.5, sm: 2 } }}>
      <Typography variant="h2" fontWeight="bold" mb={1} mt={2}>
        Converts Blotter
      </Typography>

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <Box sx={{ backgroundColor: theme.palette.background.default, mt: 3 }}>
        {activeTab === "tab1" && (
          <Box>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                backgroundColor: theme.palette.background.paper,
                p: { xs: 1, sm: 2 },
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                mx: { xs: 1, sm: 0 },
              }}
            >
              <TextField
                size="small"
                placeholder="Search blotter entries..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                sx={{
                  minWidth: { xs: 150, sm: 200 },
                  maxWidth: { xs: "100%", sm: 300 },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.background.default,
                  },
                }}
                aria-label="Search blotter entries"
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                {editMode ? (
                  <>
                    <Button
                      variant="contained"
                      color="dark"
                      onClick={handleSaveAll}
                      startIcon={<Save />}
                      size="small"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save All"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleEditModeToggle}
                      startIcon={<Cancel />}
                      size="small"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleEditModeToggle}
                      startIcon={<Edit />}
                      size="small"
                    >
                      Edit Mode
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleOpenSubmitDialog}
                      size="small"
                    >
                      Submit Trades
                    </Button>
                  </>
                )}
                {isTrader && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/Manual_Convert_Trade_Entry")}
                    size="small"
                  >
                    New Trade Entry
                  </Button>
                )}
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
                overflowX: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                maxWidth: "100vw",
                mx: { xs: 1, sm: 0 },
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress aria-label="Loading blotter entries" />
                </Box>
              ) : (
                <Table
                  size="small"
                  stickyHeader
                  aria-label="Converts Blotter Table"
                  sx={{
                    fontSize: "0.75rem",
                    "& .MuiTableCell-root": {
                      fontSize: "0.75rem",
                      padding: { xs: "2px 4px", sm: "4px 6px" },
                      borderRight: `1px solid ${theme.palette.divider}`,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { xs: "80px", sm: "auto" },
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      backgroundColor: theme.palette.grey[100],
                      fontWeight: 600,
                      fontSize: { xs: "0.78rem", sm: "0.84rem" },
                      padding: { xs: "4px 6px", sm: "6px 8px" },
                    },
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: theme.palette.grey[400],
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {TABLE_HEADERS.map(({ id, label, mobile }) => (
                        <TableCell
                          key={id}
                          sx={{
                            py: 0.5,
                            px: 1,
                            display: {
                              xs: mobile ? "table-cell" : "none",
                              lg: "table-cell",
                            },
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.key === id}
                            direction={
                              sortConfig.key === id
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort(id)}
                            aria-label={`Sort by ${label}`}
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={
                            isMobile
                              ? TABLE_HEADERS.filter((h) => h.mobile).length
                              : TABLE_HEADERS.length
                          }
                          align="center"
                        >
                          {loading ? "Loading..." : "No blotter entries found."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            backgroundColor: getRowTint(
                              row.hedge_trade_status,
                            ),
                          }}
                        >
                          {TABLE_HEADERS.map(({ id, mobile }) => (
                            <TableCell
                              key={id}
                              sx={{
                                py: { xs: 0.25, sm: 0.5 },
                                px: { xs: 0.5, sm: 1 },
                                display: {
                                  xs: mobile ? "table-cell" : "none",
                                  lg: "table-cell",
                                },
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              }}
                              title={formatFieldValue(row[id])}
                            >
                              {renderEditableCell(row, id)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Dialog
        open={submitDialogOpen}
        onClose={(event, reason) => {
          // Require an explicit button click while submitting or while
          // results are on screen — no dismissing via backdrop/Escape.
          if (submittingTrades || submitResults) return;
          setSubmitDialogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {submitResults ? "Submission Results" : "Review Hedge Entries"}
        </DialogTitle>
        <DialogContent>
          {submitResults ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Submitted {submitResults.length} hedge{" "}
                {submitResults.length === 1 ? "entry" : "entries"} as{" "}
                {submitMode === MODE_DONE ? "done trades" : "working orders"}.
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 400, overflow: "auto" }}
              >
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "4px 6px",
                      fontSize: "0.75rem",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      padding: "6px 8px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {["Symbol", "Status", "Message"].map((header) => (
                        <TableCell key={header}>{header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submitResults.map((result, index) => (
                      <TableRow key={`${result.symbol}-${index}`}>
                        <TableCell>{result.symbol}</TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            variant="body2"
                            fontWeight={600}
                            color={
                              result.status === "FAILED"
                                ? "error.main"
                                : result.status === "ENTERED_WITH_WARNINGS"
                                  ? "warning.main"
                                  : "success.main"
                            }
                          >
                            {result.status}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 300,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {result.message || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please review the following hedge entries before submission:
              </Typography>
              <ToggleButtonGroup
                value={submitMode}
                exclusive
                onChange={(event, newMode) => newMode && setSubmitMode(newMode)}
                size="small"
                disabled={submittingTrades}
                sx={{ mb: 2 }}
                aria-label="Trade submission mode"
              >
                <ToggleButton value={MODE_DONE}>
                  Book as done trades
                </ToggleButton>
                <ToggleButton value={MODE_V2}>
                  Create working orders
                </ToggleButton>
              </ToggleButtonGroup>
              {eligibleTradesToSubmit.length === 0 ? (
                <Alert severity="info">
                  No hedge entries are ready for submission.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: 400, overflow: "auto" }}
                >
                  <Table
                    size="small"
                    sx={{
                      "& .MuiTableCell-root": {
                        padding: "4px 6px",
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        padding: "6px 8px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          "Trade ID",
                          "Trade Date",
                          "Strategy",
                          "Security Action",
                          "Security Symbol",
                          "Broker",
                          "Hedge Action",
                          "Hedge Symbol",
                          "Hedge Quantity",
                          "Hedge Price",
                          "Hedge Entry",
                        ].map((header) => (
                          <TableCell key={header}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eligibleTradesToSubmit.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.trade_id}</TableCell>
                          <TableCell>{row.trade_date}</TableCell>
                          <TableCell>{row.strategy_name}</TableCell>
                          <TableCell>{row.action || row.hedge_action}</TableCell>
                          <TableCell>{row.security_symbol || row.hedge_symbol}</TableCell>
                          <TableCell>{row.BrokerCode}</TableCell>
                          <TableCell>{row.hedge_action}</TableCell>
                          <TableCell>{row.hedge_symbol}</TableCell>
                          <TableCell>{row.hedge_quantity}</TableCell>
                          <TableCell>{row.hedge_price}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 200,
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {`${row.hedge_action} ${row.hedge_quantity} of ${row.hedge_symbol} at ${row.hedge_price}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {submitResults ? (
            <Button onClick={handleDismissResults} variant="contained">
              Dismiss
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setSubmitDialogOpen(false)}
                disabled={submittingTrades}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                variant="contained"
                color="success"
                disabled={submittingTrades || eligibleTradesToSubmit.length === 0}
                startIcon={
                  submittingTrades ? <CircularProgress size={16} /> : null
                }
              >
                {submittingTrades
                  ? `Submitting ${submitProgress.completed} of ${submitProgress.total}...`
                  : submitMode === MODE_DONE
                    ? "Submit as Done Trades"
                    : "Submit as Working Orders"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
